import {
  DELIVERY_FROM_DATE,
  DELIVERY_TO_DATE,
  FILTER_CLOCKED_HOURS,
  FILTER_OVER_CLOCKED_HOURS,
  FROMDATE,
  GENERIC_SEARCH,
  INCLUDE_FINALIZED,
  PICKUP_FROM_DATE,
  PICKUP_TO_DATE,
  SCHEDULE_FROM_DATE,
  SCHEDULE_TO_DATE,
  SELECTED_WO_CUSTOMERS,
  SELECTED_WO_LOCS,
  SELECTED_WO_SALES,
  SELECTED_WO_PRIORITIES,
  SELECTED_WO_statuses,
  SELECTED_WO_TECHS,
  TODATE,
  VIEW,
} from "components/common/constants/filterParamConstants";
import { Customer } from "../../models/workOrderTypes";
import {
  ExtractSavableFiltersInput,
  SavedCustomerSelection,
  SavedFilterValues,
} from "../types/FilterTemplateTypes";

export const DEFAULT_SAVED_FILTER_VALUES: SavedFilterValues = {
  view: 0,
  woStatuses: null,
  woTechs: null,
  woLocations: null,
  woSalesPersons: null,
  woPriorities: null,
  woCustomers: [],
  woCustomerDetails: [],
  searchString: "",
  workOrderCreationStartDate: null,
  workOrderCreationEndDate: null,
  workOrderScheduleFromDate: null,
  workOrderScheduleToDate: null,
  workOrderDeliveryStartDate: null,
  workOrderDeliveryEndDate: null,
  workOrderPickupStartDate: null,
  workOrderPickupEndDate: null,
  filterClockedHours: false,
  filterOverClockedHours: false,
  includeFinalized: false,
};

const UI_ONLY_FILTER_KEYS = new Set([
  "boardZoom",
  "collapseWorkOrders",
  "userIdentity",
]);

const DATE_RANGE_KEYS: Array<[string, string]> = [
  [FROMDATE, TODATE],
  [SCHEDULE_FROM_DATE, SCHEDULE_TO_DATE],
  [DELIVERY_FROM_DATE, DELIVERY_TO_DATE],
  [PICKUP_FROM_DATE, PICKUP_TO_DATE],
];

const toDateString = (value: unknown): string | null => {
  if (!value) {
    return null;
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().slice(0, 10);
  }

  if (typeof value === "string" && value.trim().length > 0) {
    return value;
  }

  return null;
};

const toMultiSelectValue = (value: unknown): string[] | null => {
  if (value === null || value === undefined) {
    return null;
  }

  if (Array.isArray(value)) {
    return value.length > 0 ? value.map(String) : null;
  }

  return null;
};

const toLocalMultiSelectState = (value: string[] | null | undefined): string[] => {
  if (!value || value.length === 0) {
    return ["0"];
  }

  return value;
};

const toDateRange = (
  fromValue?: string | null,
  toValue?: string | null,
): [Date, Date] | null => {
  if (!fromValue || !toValue) {
    return null;
  }

  const fromDate = new Date(fromValue);
  const toDate = new Date(toValue);

  if (
    Number.isNaN(fromDate.getTime()) ||
    Number.isNaN(toDate.getTime()) ||
    toDate.getTime() < fromDate.getTime()
  ) {
    return null;
  }

  return [fromDate, toDate];
};

export const extractSavableFilters = ({
  filters,
  view,
  selectedCustomers = [],
}: ExtractSavableFiltersInput): SavedFilterValues => {
  const saved: SavedFilterValues = {
    view,
    woStatuses: toMultiSelectValue(filters[SELECTED_WO_statuses]),
    woTechs: toMultiSelectValue(filters[SELECTED_WO_TECHS]),
    woLocations: toMultiSelectValue(filters[SELECTED_WO_LOCS]),
    woSalesPersons: toMultiSelectValue(filters[SELECTED_WO_SALES]),
    woPriorities: toMultiSelectValue(filters[SELECTED_WO_PRIORITIES]),
    woCustomers: Array.isArray(filters[SELECTED_WO_CUSTOMERS])
      ? (filters[SELECTED_WO_CUSTOMERS] as number[])
      : selectedCustomers.map((customer) => customer.identity),
    woCustomerDetails: selectedCustomers.map((customer) => ({
      identity: customer.identity,
      name: customer.name,
    })),
    searchString:
      typeof filters[GENERIC_SEARCH] === "string"
        ? filters[GENERIC_SEARCH]
        : "",
    workOrderCreationStartDate: toDateString(filters[FROMDATE]),
    workOrderCreationEndDate: toDateString(filters[TODATE]),
    workOrderScheduleFromDate: toDateString(filters[SCHEDULE_FROM_DATE]),
    workOrderScheduleToDate: toDateString(filters[SCHEDULE_TO_DATE]),
    workOrderDeliveryStartDate: toDateString(filters[DELIVERY_FROM_DATE]),
    workOrderDeliveryEndDate: toDateString(filters[DELIVERY_TO_DATE]),
    workOrderPickupStartDate: toDateString(filters[PICKUP_FROM_DATE]),
    workOrderPickupEndDate: toDateString(filters[PICKUP_TO_DATE]),
    filterClockedHours: Boolean(filters[FILTER_CLOCKED_HOURS]),
    filterOverClockedHours: Boolean(filters[FILTER_OVER_CLOCKED_HOURS]),
    includeFinalized: Boolean(filters[INCLUDE_FINALIZED]),
  };

  return saved;
};

export const persistSavedFiltersToLocalStorage = (
  saved: SavedFilterValues,
  userName: string,
): void => {
  localStorage.setItem(`${VIEW}_${userName}`, String(saved.view ?? 0));
  localStorage.setItem(
    `${GENERIC_SEARCH}_${userName}`,
    saved.searchString ?? "",
  );

  const persistMultiSelect = (
    key: string,
    value: string[] | null | undefined,
  ) => {
    const localValue = toLocalMultiSelectState(value ?? null);
    localStorage.setItem(`${key}_${userName}`, localValue.join(","));
  };

  persistMultiSelect(SELECTED_WO_statuses, saved.woStatuses);
  persistMultiSelect(SELECTED_WO_TECHS, saved.woTechs);
  persistMultiSelect(SELECTED_WO_LOCS, saved.woLocations);
  persistMultiSelect(SELECTED_WO_SALES, saved.woSalesPersons);
  persistMultiSelect(SELECTED_WO_PRIORITIES, saved.woPriorities);

  const customerDetails = saved.woCustomerDetails ?? [];
  if (customerDetails.length > 0) {
    localStorage.setItem(
      `${SELECTED_WO_CUSTOMERS}_${userName}`,
      JSON.stringify(customerDetails),
    );
  } else {
    localStorage.removeItem(`${SELECTED_WO_CUSTOMERS}_${userName}`);
  }

  DATE_RANGE_KEYS.forEach(([fromKey, toKey]) => {
    const fromValue = saved[fromKey as keyof SavedFilterValues] as
      | string
      | null
      | undefined;
    const toValue = saved[toKey as keyof SavedFilterValues] as
      | string
      | null
      | undefined;

    if (fromValue && toValue) {
      localStorage.setItem(
        `${fromKey}_${userName}`,
        JSON.stringify({ from: fromValue, to: toValue }),
      );
    } else {
      localStorage.removeItem(`${fromKey}_${userName}`);
    }
  });

  localStorage.setItem(
    `${FILTER_CLOCKED_HOURS}_${userName}`,
    String(Boolean(saved.filterClockedHours)),
  );
  localStorage.setItem(
    `${FILTER_OVER_CLOCKED_HOURS}_${userName}`,
    String(Boolean(saved.filterOverClockedHours)),
  );
  localStorage.setItem(
    `${INCLUDE_FINALIZED}_${userName}`,
    String(Boolean(saved.includeFinalized)),
  );
};

export const applySavedFiltersToStore = (
  saved: SavedFilterValues,
  setFilters: (key: string, value: unknown) => void,
): void => {
  setFilters(VIEW, saved.view ?? 0);
  setFilters(GENERIC_SEARCH, saved.searchString ?? "");
  setFilters(SELECTED_WO_statuses, saved.woStatuses ?? null);
  setFilters(SELECTED_WO_TECHS, saved.woTechs ?? null);
  setFilters(SELECTED_WO_LOCS, saved.woLocations ?? null);
  setFilters(SELECTED_WO_SALES, saved.woSalesPersons ?? null);
  setFilters(SELECTED_WO_PRIORITIES, saved.woPriorities ?? null);
  setFilters(
    SELECTED_WO_CUSTOMERS,
    saved.woCustomers && saved.woCustomers.length > 0
      ? saved.woCustomers
      : saved.woCustomerDetails && saved.woCustomerDetails.length > 0
        ? saved.woCustomerDetails.map((customer) => customer.identity)
        : null,
  );
  setFilters(FROMDATE, saved.workOrderCreationStartDate ?? null);
  setFilters(TODATE, saved.workOrderCreationEndDate ?? null);
  setFilters(SCHEDULE_FROM_DATE, saved.workOrderScheduleFromDate ?? null);
  setFilters(SCHEDULE_TO_DATE, saved.workOrderScheduleToDate ?? null);
  setFilters(DELIVERY_FROM_DATE, saved.workOrderDeliveryStartDate ?? null);
  setFilters(DELIVERY_TO_DATE, saved.workOrderDeliveryEndDate ?? null);
  setFilters(PICKUP_FROM_DATE, saved.workOrderPickupStartDate ?? null);
  setFilters(PICKUP_TO_DATE, saved.workOrderPickupEndDate ?? null);
  setFilters(FILTER_CLOCKED_HOURS, Boolean(saved.filterClockedHours));
  setFilters(FILTER_OVER_CLOCKED_HOURS, Boolean(saved.filterOverClockedHours));
  setFilters(INCLUDE_FINALIZED, Boolean(saved.includeFinalized));
};

export interface AppliedFilterTemplateLocalState {
  view: number;
  searchString: string;
  woStatuses: string[];
  woTechs: string[];
  woLocations: string[];
  woSalesPersons: string[];
  woPriorities: string[];
  selectedCustomers: Customer[];
  woCreationDate: [Date, Date] | null;
  scheduledDate: [Date, Date] | null;
  woDeliveryDate: [Date, Date] | null;
  woPickupDate: [Date, Date] | null;
  filterClockedHours: boolean;
  filterOverClockedHours: boolean;
  includeFinalized: boolean;
}

const toCustomerSelection = (
  customer: SavedCustomerSelection,
): Customer => ({
  identity: customer.identity,
  name: customer.name,
  number: "",
  creditHold: false,
  customerPhone: "",
  customerEmail: "",
  customerAddress: "",
});

export const mapSavedFiltersToLocalState = (
  saved: SavedFilterValues,
): AppliedFilterTemplateLocalState => ({
  view: saved.view ?? 0,
  searchString: saved.searchString ?? "",
  woStatuses: toLocalMultiSelectState(saved.woStatuses),
  woTechs: toLocalMultiSelectState(saved.woTechs),
  woLocations: toLocalMultiSelectState(saved.woLocations),
  woSalesPersons: toLocalMultiSelectState(saved.woSalesPersons),
  woPriorities: toLocalMultiSelectState(saved.woPriorities),
  selectedCustomers: (saved.woCustomerDetails ?? []).map(toCustomerSelection),
  woCreationDate: toDateRange(
    saved.workOrderCreationStartDate,
    saved.workOrderCreationEndDate,
  ),
  scheduledDate: toDateRange(
    saved.workOrderScheduleFromDate,
    saved.workOrderScheduleToDate,
  ),
  woDeliveryDate: toDateRange(
    saved.workOrderDeliveryStartDate,
    saved.workOrderDeliveryEndDate,
  ),
  woPickupDate: toDateRange(
    saved.workOrderPickupStartDate,
    saved.workOrderPickupEndDate,
  ),
  filterClockedHours: Boolean(saved.filterClockedHours),
  filterOverClockedHours: Boolean(saved.filterOverClockedHours),
  includeFinalized: Boolean(saved.includeFinalized),
});

export const isUiOnlyFilterKey = (key: string): boolean =>
  UI_ONLY_FILTER_KEYS.has(key);
